import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  IoNotificationsOutline,
  IoPersonOutline,
  IoSchoolOutline,
  IoMailOutline,
  IoChatbubbleEllipsesOutline,
  IoSaveOutline,
} from "react-icons/io5";
import ProfileSwitch from "../components/ProfileSwitch";
import ProfileInput from "../components/ProfileInput";
import ProfileSkeleton from "../components/skeleton-loading/ProfileSkeleton";
import AlertModal from "../components/AlertModal"; // AlertModal eklendi
import { useData } from "../data/DataProvider";
import { studentServices } from "../api/studentServices";

export default function Profile() {
  const navigate = useNavigate();

  const { studentData, getAllData, loading } = useData();

  const [parentName, setParentName] = useState("");
  const [parentPhone, setParentPhone] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [parentAddress, setParentAddress] = useState("");

  const [isPushEnabled, setIsPushEnabled] = useState(true);
  const [isEmailEnabled, setIsEmailEnabled] = useState(true);
  const [isSmsEnabled, setIsSmsEnabled] = useState(false);

  const [isSaving, setIsSaving] = useState(false);

  // Modal State Yönetimi
  const [modalVisible, setModalVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: "",
    message: "",
    type: "success",
  });

  const showAlert = (title, message, type = "success") => {
    setModalConfig({ title, message, type });
    setModalVisible(true);
  };

  useEffect(() => {
    if (getAllData) {
      getAllData();
    }
  }, []);

  useEffect(() => {
    if (studentData) {
      if (studentData.parent) {
        const parentFullName =
          `${studentData.parent.name || ""} ${studentData.parent.surname || ""}`.trim();
        setParentName(parentFullName);
        setParentPhone(studentData.parent.phone || "");
        setParentEmail(studentData.parent.email || studentData.email || "");
      }

      setParentAddress(studentData.address || "");

      setIsPushEnabled(
        Boolean(
          studentData.is_push_active ?? studentData.parent?.is_push_active,
        ),
      );
      setIsEmailEnabled(
        Boolean(
          studentData.is_email_active ?? studentData.parent?.is_email_active,
        ),
      );
      setIsSmsEnabled(
        Boolean(studentData.is_sms_active ?? studentData.parent?.is_sms_active),
      );
    }
  }, [studentData]);

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    const payload = {
      parent_phone: parentPhone,
      parent_email: parentEmail,
      student_address: parentAddress,

      is_push_active: isPushEnabled,
      is_email_active: isEmailEnabled,
      is_sms_active: isSmsEnabled,
    };

    try {
      await studentServices.updateProfile(payload);

      if (getAllData) {
        await getAllData(true);
      }

      // Başarılı uyarısı göster (Yönlendirme iptal edildi)
      showAlert(
        "Başarılı",
        "Profil bilgileriniz başarıyla güncellendi.",
        "success",
      );
    } catch (error) {
      console.error("Profil güncellenirken hata oluştu:", error);
      // Hata uyarısı göster
      showAlert(
        "Güncelleme Başarısız",
        "Kayıt sırasında bir hata oluştu. Lütfen tekrar deneyiniz.",
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (loading && !studentData) {
    return <ProfileSkeleton />;
  }

  return (
    <div className="min-h-screen bg-[#f8f9ff] antialiased pb-24">
      <main className="max-w-xl mx-auto px-4 pt-6 space-y-6">
        <form onSubmit={handleSave} className="space-y-6">
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-[#00306b] font-bold text-xs tracking-wider uppercase pl-1">
              <IoPersonOutline className="text-base" />
              <span>Veli Bilgileri</span>
            </div>

            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-[0px_2px_8px_rgba(0,0,0,0.015)] space-y-4">
              <ProfileInput
                label="Adı Soyadı"
                type="text"
                value={parentName}
                onChange={(e) => setParentName(e.target.value)}
                disabled
                className="bg-gray-50 cursor-not-allowed opacity-75"
              />

              <ProfileInput
                label="Öğrenci Adresi"
                type="text"
                value={parentAddress}
                onChange={(e) => setParentAddress(e.target.value)}
              />

              <ProfileInput
                label="Telefon Numarası"
                type="tel"
                value={parentPhone}
                onChange={(e) => setParentPhone(e.target.value)}
              />

              <ProfileInput
                label="E-posta Adresi"
                type="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-[#00306b] font-bold text-xs tracking-wider uppercase pl-1">
              <IoSchoolOutline className="text-base" />
              <span>Öğrenci Bilgileri</span>
            </div>

            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-[0px_2px_8px_rgba(0,0,0,0.015)] space-y-4">
              <div className="grid grid-cols-2 gap-y-4 gap-x-2">
                <div>
                  <span className="text-xs font-medium text-gray-500 block">
                    Öğrenci Adı
                  </span>
                  <span className="text-sm font-bold text-gray-900 mt-0.5 block">
                    {studentData?.name && studentData?.surname
                      ? `${studentData.name} ${studentData.surname}`
                      : "-"}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-medium text-gray-500 block">
                    Öğrenci No
                  </span>
                  <span className="text-sm font-bold text-gray-900 mt-0.5 block">
                    {studentData?.student_number
                      ? `#${studentData.student_number}`
                      : "-"}
                  </span>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 block">
                    Adres / Konum
                  </span>
                  <span className="text-sm font-bold text-gray-900 mt-0.5 block">
                    {parentAddress || studentData?.address || "-"}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-medium text-gray-500 block">
                    Sınıf / Derece
                  </span>
                  <span className="text-sm font-bold text-gray-900 mt-0.5 block">
                    {studentData?.grade_number || "-"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-[#00306b] font-bold text-xs tracking-wider uppercase pl-1">
              <IoNotificationsOutline className="text-base" />
              <span>Bildirim Tercihleri</span>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-[0px_2px_8px_rgba(0,0,0,0.015)] divide-y divide-gray-100">
              <ProfileSwitch
                title="Mobil Bildirim"
                description="Anlık servis ve ödeme duyuruları"
                icon={IoNotificationsOutline}
                checked={isPushEnabled}
                onChange={() => setIsPushEnabled(!isPushEnabled)}
              />

              <ProfileSwitch
                title="E-posta"
                description="Aylık makbuz ve ekstre bilgileri"
                icon={IoMailOutline}
                checked={isEmailEnabled}
                onChange={() => setIsEmailEnabled(!isEmailEnabled)}
              />

              <ProfileSwitch
                title="SMS Bildirimi"
                description="Acil servis gecikme haberleri"
                icon={IoChatbubbleEllipsesOutline}
                checked={isSmsEnabled}
                onChange={() => setIsSmsEnabled(!isSmsEnabled)}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isSaving}
            className="w-full bg-[#00306b] hover:bg-[#002452] active:scale-[0.99] disabled:opacity-50 text-white text-sm font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all duration-150 mt-8 outline-none cursor-pointer"
          >
            <IoSaveOutline className="text-lg" />
            <span>
              {isSaving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
            </span>
          </button>
        </form>
      </main>

      {/* AlertModal Bileşeni */}
      <AlertModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
        buttonText="Tamam"
      />
    </div>
  );
}
